// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';
import {
  LocalStorageContext,
  LocalStorageContextProps,
  LocalStorageStateProps,
} from './LocalStorageContext';

interface LocalStorageProviderProps {
  children: React.ReactNode;
}

export const LocalStorageProvider: React.FC<LocalStorageProviderProps> = ({ children }) => {
  const [localStorageState, setLocalStorage] = React.useState({} as LocalStorageStateProps);
  const getStateFromLocalStorage = () => {
    const pollingEnabled = localStorage.getItem('pollingEnabled');
    const webSocketEnabled = localStorage.getItem('webSocketEnabled');

    const isDark = localStorage.getItem('isDark');
    const activeContractsLogsEnabled = localStorage.getItem('activeContractsLogsEnabled');

    setLocalStorage((p) => ({
      ...p,
      pollingEnabled: pollingEnabled === null || pollingEnabled === 'true',
      webSocketEnabled: webSocketEnabled === null || webSocketEnabled === 'true',
      isDark: isDark === null || isDark === 'true',
      activeContractsLogsEnabled:
        activeContractsLogsEnabled === null || activeContractsLogsEnabled === 'true',
    }));
  };
  const setLocalStorageState = React.useCallback((key: string, value: string) => {
    localStorage.setItem(key, value);

    if (key === 'pollingEnabled') {
      setLocalStorage((p) => ({
        ...p,
        pollingEnabled: value === 'true',
      }));
    }
    if (key === 'webSocketEnabled') {
      setLocalStorage((p) => ({
        ...p,
        webSocketEnabled: value === 'true',
      }));
    }
    if (key === 'isDark') {
      setLocalStorage((p) => ({
        ...p,
        isDark: value === 'true',
      }));
    }

    if (key === 'activeContractsLogsEnabled') {
      setLocalStorage((p) => ({
        ...p,
        activeContractsLogsEnabled: value === 'true',
      }));
    }
  }, []);
  React.useEffect(() => {
    getStateFromLocalStorage();
  }, [
    localStorageState.pollingEnabled,
    localStorageState.webSocketEnabled,
    localStorageState.isDark,
    localStorageState.activeContractsLogsEnabled,
  ]);
  const value: LocalStorageContextProps = React.useMemo(
    () => ({
      localStorageState,
      setLocalStorageState,
    }),
    [setLocalStorageState, localStorageState],
  );
  return <LocalStorageContext.Provider value={value}>{children}</LocalStorageContext.Provider>;
};
